import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useBox } from '../context/BoxContext';
import { productService1 } from '../services/firebaseService';

// Add fuse.js for fuzzy search
import Fuse from 'fuse.js';

const PAGE_SIZE = 12;

const Products = () => {
  const { addToBox } = useBox();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Pagination state
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);

  // Filter state
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showFilter, setShowFilter] = useState(false);

  // Price filter state (single bar)
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(4350);
  const [currentPrice, setCurrentPrice] = useState(4350);

  // --- NEW: Store all products for fuzzy searching
  const [allProducts, setAllProducts] = useState([]);

  // Load all categories + min/max price on mount
  useEffect(() => {
    const fetchCategoriesAndPrice = async () => {
      try {
        const all = await productService1.getProductsByPage(10000, 1, '');
        setAllProducts(all); // <-- Store for fuzzy search

        // Support extracting from both category (string) and categories (array)
        const cats = Array.from(new Set(
          (all || [])
            .flatMap(p =>
              Array.isArray(p.categories)
                ? p.categories
                : (p.category ? [p.category] : [])
            )
            .filter(Boolean)
        ));
        setCategories(cats);
        if (all.length > 0) {
          const prices = all.map(p => Number(p.price) || 0);
          const min = Math.min(...prices);
          const max = Math.max(...prices);
          setMinPrice(min);
          setMaxPrice(max);
          setCurrentPrice(max);
        }
      } catch {
        setCategories([]);
        setMinPrice(0);
        setMaxPrice(4350);
        setCurrentPrice(4350);
        setAllProducts([]);
      }
    };
    fetchCategoriesAndPrice();
  }, []);

  // Whenever page, searchTerm, selectedCategory, or currentPrice changes, (re-)load products
  useEffect(() => {
    loadProducts(page, searchTerm, selectedCategory, currentPrice);
    // eslint-disable-next-line
  }, [page, searchTerm, selectedCategory, currentPrice, allProducts]);

  // Backend-powered filtering and pagination
  const loadProducts = async (pageNum = 1, search = '', category = '', price = currentPrice) => {
    try {
      setLoading(true);
      setProducts([]);
      let data, count;
      // --- 1. Use allProducts for client-side search (with fuzzy)
      data = allProducts;

      // --- 2. Fuzzy search by name/description/category
      if (search) {
        // Fuzzy search options
        const fuse = new Fuse(data, {
          keys: [
            { name: 'name', weight: 0.6 },
            { name: 'description', weight: 0.25 },
            { name: 'category', weight: 0.1 },
            { name: 'categories', weight: 0.05 }
          ],
          threshold: 0.38, // adjust for more(/less) tolerance
          minMatchCharLength: 2,
          includeScore: false
        });
        const results = fuse.search(search);
        data = results.map(r => r.item);
      }

      // Category filter: support array and string
      if (category) {
        data = data.filter(p =>
          (Array.isArray(p.categories) && p.categories.includes(category)) ||
          p.category === category
        );
      }
      // Filter by price (up to max selected)
      data = data.filter(p => {
        const priceNum = Number(p.price) || 0;
        return priceNum >= minPrice && priceNum <= price;
      });
      count = data.length;
      // Paginate
      const paged = data.slice((pageNum - 1) * PAGE_SIZE, pageNum * PAGE_SIZE);
      setProducts(paged);
      setTotalProducts(count);
      setError(null);
    } catch (err) {
      console.error('❌ Erreur lors du chargement des produits :', err);
      setError(err.message || 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'MAD'
    }).format(price);
  };

  const totalPages = Math.ceil(totalProducts / PAGE_SIZE);

  return (
    <div className="products-page">
      <div className="container">
        <div className="page-header">
          <h1><i className="fas fa-pills"></i> Nos produits</h1>
          <p>Découvrez toute notre gamme de produits El Hiba Para</p>
        </div>

        {/* Search + Filter Section */}
        <div className="search-section" style={{ position: 'relative', marginBottom: 20 }}>
          <div
            className="search-container"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              flexWrap: 'wrap',
              rowGap: 8
            }}
          >
            <div
              className="search-input-wrapper"
              style={{
                flex: 1,
                minWidth: 170,
                maxWidth: "100%",
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <i className="fas fa-search search-icon"></i>
              <input
                type="text"
                className="search-input"
                placeholder="Recherchez un produit par nom, description ou catégorie..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                style={{ width: '100%' }}
              />
              {searchTerm && (
                <button
                  className="clear-search-btn"
                  onClick={() => {
                    setSearchTerm('');
                    setPage(1);
                  }}
                  title="Effacer la recherche"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
            {/* Filter Button: Mobile friendly */}
            <button
              style={{
                marginLeft: 6,
                border: 'none',
                background: '#22223B',
                color: '#fff',
                borderRadius: 8,
                padding: '8px 19px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 700,
                fontSize: 16,
                boxShadow: '0 1px 4px #e8e8f8',
                transition: 'background .18s',
                minWidth: 92,
                minHeight: 40,
                justifyContent: 'center',
              }}
              onClick={() => setShowFilter(f => !f)}
            >
              <i className="fas fa-filter" style={{ marginRight: 7, fontSize: 17 }} />
              <span style={{ display: 'inline-block', lineHeight: 1 }}>Filtrer</span>
            </button>
          </div>
          {/* Prix Filter under search bar */}
          <div className="prix-filter" style={{
            margin: '32px auto 0 auto',
            maxWidth: 410,
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 2px 16px 0 #e6eaff',
            padding: '20px 28px 16px 28px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            border: '1px solid #e5e8fa'
          }}>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 7, color: '#263087', letterSpacing: 0.4 }}>Prix</div>
            <div style={{ color: '#8b8fa7', fontSize: 15, marginBottom: 12, fontWeight: 500 }}>
              {formatPrice(minPrice)} - {formatPrice(maxPrice)}
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              gap: 18,
              marginBottom: 5
            }}>
              <input
                type="range"
                min={minPrice}
                max={maxPrice}
                value={currentPrice}
                onChange={e => {
                  setCurrentPrice(Number(e.target.value));
                  setPage(1);
                }}
                style={{
                  flex: 1,
                  accentColor: "#3F51B5",
                  height: 4,
                  background: 'transparent'
                }}
              />
              <span style={{
                minWidth: 78,
                fontSize: 15,
                fontWeight: 700,
                color: '#263087',
                background: '#f3f6ff',
                borderRadius: 7,
                padding: '2px 12px 2px 9px',
                boxShadow: '0 1px 6px #f3f6ff'
              }}>{formatPrice(currentPrice)}</span>
            </div>
          </div>
          {/* Dropdown Filter Panel */}
          {showFilter && (
            <div
              style={{
                position: 'absolute',
                top: 55,
                right: 0,
                zIndex: 10,
                minWidth: 280,
                background: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: 14,
                boxShadow: '0 8px 32px rgba(80,90,130,0.13)',
                padding: '22px 20px 14px 20px',
                display: 'flex',
                flexDirection: 'column',
                gap: 20
              }}
            >
              <div>
                <label style={{ fontWeight: 700, fontSize: 16, color: '#222', marginBottom: 7, display: 'block' }}>Catégorie</label>
                <select
                  value={selectedCategory}
                  onChange={e => {
                    setSelectedCategory(e.target.value);
                    setPage(1);
                  }}
                  style={{
                    width: '100%',
                    marginTop: 6,
                    border: '1.8px solid #c9d0e3',
                    borderRadius: 8,
                    height: 40,
                    fontSize: 16,
                    background: '#f6f8fa',
                    color: '#222',
                    padding: '0 8px'
                  }}
                >
                  <option value="">Toutes les catégories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <button
                className="btn btn-outline"
                onClick={() => {
                  setSelectedCategory('');
                  setShowFilter(false);
                  setPage(1);
                }}
                style={{
                  marginTop: 10,
                  fontSize: 15,
                  padding: '8px 0',
                  border: 'none',
                  background: '#f3f6ff',
                  borderRadius: 8,
                  fontWeight: 600,
                  color: '#3F51B5',
                  letterSpacing: 0.5,
                  boxShadow: '0 0 0 1px #e0e0e0',
                  justifyContent: 'center'
                }}
              >
                Réinitialiser les filtres
              </button>
            </div>
          )}
          <div className="search-info" style={{ marginTop: 10 }}>
            {(searchTerm || selectedCategory)
              ? <p>
                  {totalProducts} résultat{totalProducts !== 1 ? 's' : ''} trouvé{totalProducts !== 1 ? 's' : ''}
                  {(searchTerm && ` pour "${searchTerm}"`) || ''}
                </p>
              : <p>Affichage de {totalProducts} produits</p>
            }
          </div>
        </div>
        {/* End Search + Filter Section */}

        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"><div className="spinner"></div></div>
            <p className="loading-message">Chargement des produits...</p>
          </div>
        )}

        {error && (
          <div className="error">
            <h3>Erreur lors du chargement des produits</h3>
            <p>Erreur : {error}</p>
            <button onClick={() => loadProducts(page, searchTerm, selectedCategory, currentPrice)} className="btn btn-primary">
              Réessayer
            </button>
          </div>
        )}

        {!loading && !error && products.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h3>Aucun produit trouvé</h3>
            <p>{
              searchTerm
                ? `Aucun produit ne correspond à votre recherche pour "${searchTerm}"`
                : 'La liste des produits est vide.'
            }</p>
            {(searchTerm || selectedCategory) && (
              <button onClick={() => {
                setSearchTerm('');
                setSelectedCategory('');
                setPage(1);
                setCurrentPrice(maxPrice);
              }} className="btn btn-primary">
                Effacer la recherche et les filtres
              </button>
            )}
          </div>
        )}

        {!loading && !error && products.length > 0 && (
          <>
            {/* Pagination Controls (top) */}
            <div style={{ textAlign: 'center', margin: '30px 0' }}>
              <button 
                className="btn btn-primary"
                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                disabled={page === 1}
                style={{ marginRight: '10px' }}
              >
                Précédent
              </button>
              <span>Page {page} sur {totalPages}</span>
              <button 
                className="btn btn-primary"
                onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                disabled={page === totalPages}
                style={{ marginLeft: '10px' }}
              >
                Suivant
              </button>
            </div>

            <div className="products-grid">
              {products.map(product => (
                <div key={product.id} className={`product-card`}>
                  <div className="product-image-container">
                    <img
                      src={product.imageUrl || 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=500&h=300&fit=crop'}
                      alt={product.name}
                      className="product-image"
                    />

                    <div className="product-overlay">
                      <Link to={`/products/${product.id}`} className="btn btn-primary btn-view-product">
                        <i className="fas fa-eye"></i> Voir le détail
                      </Link>
                    </div>
                  </div>
                  <div className="product-info">
                    <h3 className="product-name">{product.name}</h3>
                    {/* Show all categories if present */}
                    <p className="product-subtitle">
                      {Array.isArray(product.categories)
                        ? product.categories.join(', ')
                        : (product.category || '')}
                    </p>
                    <p className="product-price">
                        <span className="current-price">{formatPrice(product.price)}</span>
                        {' '}
                        <span className="old-price">
                        {formatPrice(product.price + 20)}
                        </span>
                    </p>
                    <div className="product-stock">
                      <span className={`stock-badge ${product.stockQuantity > 0 ? 'in-stock' : 'out-of-stock'}`}>
                        <i className={`fas ${product.stockQuantity > 0 ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                        {product.stockQuantity > 0 ? `En stock` : 'Rupture de stock'}
                      </span>
                    </div>
                    <p className="product-description">
                      {product.description?.substring(0, 120)}...
                    </p>
                    <div className="product-actions">
                      <button
                        onClick={() => addToBox(product, 1)}
                        className="btn btn-primary btn-full"
                        disabled={product.stockQuantity <= 0}
                      >
                        <i className="fas fa-shopping-cart"></i> {product.stockQuantity > 0 ? 'Ajouter au panier' : 'Rupture de stock'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Pagination Controls (bottom) */}
            <div style={{ textAlign: 'center', margin: '30px 0' }}>
              <button 
                className="btn btn-primary"
                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                disabled={page === 1}
                style={{ marginRight: '10px' }}
              >
                Précédent
              </button>
              <span>Page {page} sur {totalPages}</span>
              <button 
                className="btn btn-primary"
                onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                disabled={page === totalPages}
                style={{ marginLeft: '10px' }}
              >
                Suivant
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Products;